/**
 * One tenant, as the vendor sees it (container, phase 7b): its detail, and
 * suspend / reactivate — and the staff's notes on it (D39: kind, contact,
 * internal notes; never the tenant's). Live through the `tenant` change kind.
 */
import { toast } from 'sonner';
import { TenantRecordConflict } from '../api/vendor.js';
import { useNow, useSaveTenantRecord, useVendorTenant } from '../app/hooks.js';
import { TenantRecordCard } from '../components/vendor/TenantRecordCard.js';
import { SuspendTenantDialog } from '../components/vendor/SuspendTenantDialog.js';
import { TenantDetail } from '../components/vendor/TenantDetail.js';
import { Button } from '../components/ui/button.js';
import { useVendorAdmin } from './VendorLayout.js';
import { useTenantStatusActions } from './VendorTenantsView.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function VendorTenantView({ tenantId }: { readonly tenantId: string }) {
  const detail = useVendorTenant(tenantId);
  const canAdmin = useVendorAdmin();
  const now = useNow(30_000);
  const status = useTenantStatusActions();
  const save = useSaveTenantRecord();
  if (detail.data === undefined) {
    if (detail.error !== null) {
      return (
        <div className="grid gap-4" role="alert" data-testid="vendor-tenant-failed">
          <p className="text-sm text-destructive">Could not load the tenant: {messageOf(detail.error)}</p>
          <Button variant="outline" className="w-fit" onClick={() => void detail.refetch()}>
            Retry
          </Button>
        </div>
      );
    }
    return <p className="p-8 text-center text-sm text-muted-foreground">Loading the tenant…</p>;
  }
  const { tenant, record } = detail.data;
  const conflict = save.error instanceof TenantRecordConflict;
  return (
    <>
      <TenantDetail detail={detail.data} canAdmin={canAdmin} nowMs={now} onSuspend={() => status.open('suspend', tenant.tenantId)} onReactivate={() => status.open('reactivate', tenant.tenantId)} />
      {record === undefined ? null : (
        <TenantRecordCard
          // One draft per tenant: the card follows the record's changes itself (dirty-aware, D35).
          key={tenant.tenantId}
          record={record}
          canEdit={canAdmin}
          pending={save.isPending}
          error={save.error === null || conflict ? null : messageOf(save.error)}
          conflict={conflict}
          onSave={(edit, ifUpdatedAt) =>
            save.mutate(
              { tenantId: tenant.tenantId, edit: { contact: edit.contact, notes: edit.notes, ifUpdatedAt } },
              {
                onSuccess: () => toast.success(`Saved the notes on ${tenant.tenantId}`),
                // Lost to someone else's save: read theirs, so the card can offer it.
                onError: (err) => {
                  if (err instanceof TenantRecordConflict) void detail.refetch();
                },
              },
            )
          }
          onResolve={() => save.reset()}
        />
      )}
      <SuspendTenantDialog mode={status.action?.mode ?? null} tenant={{ tenantId: tenant.tenantId, name: tenant.name }} pending={status.pending} error={status.error} onClose={status.close} onConfirm={status.confirm} />
    </>
  );
}
